import { apiCustomer } from "@/api";
import { createEmptyCart } from "@/atoms/cart";
import { cartSyncEnabled } from "@/consts";
import { BasicInfo, Cart, Category, Item, ItemOffer, OptionGroup } from "@/types";
import { buildCartItemOptionsPayload } from "./build-checkout-cart-items";
import { CartReconstructionService } from "./cart-reconstruction-service";
import { findCategoryForOffer } from "./find-category-for-offer";
import { getMerchantIdOrThrow } from "./company-storage";
import { CartItemData } from "./cart-sync/types";

export interface CartBackendService {
  saveCart: (cart: Cart, categories: Category[]) => Promise<any>;
  loadCart: (reconstructionData?: {
    items: Item[];
    itemOffers: ItemOffer[];
    optionGroups: OptionGroup[];
    basicInfo: BasicInfo | null;
  }) => Promise<Cart | null>;
  clearCart: () => Promise<void>;
  updateCartItemQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeCartItem: (itemId: string) => Promise<void>;
  addCartItem: (item: CartItemData) => Promise<{ id: string }>;
}

export const cartBackendService: CartBackendService = {
  async saveCart(cart: Cart, categories: Category[]): Promise<void> {
    if (!cartSyncEnabled) return;

    const findCategoryId = (itemOfferId: string): string | undefined =>
      findCategoryForOffer(categories, itemOfferId)?.id;

    const cartData = {
      items: cart.items.map((cartItem) => ({
        id: cartItem.item?.id || cartItem.itemOffer?.itemId || "",
        categoryId: findCategoryId(cartItem.itemOffer.id),
        itemOfferId: cartItem.itemOffer.id,
        quantity: cartItem.quantity,
        options: buildCartItemOptionsPayload(cartItem),
        specialInstructions: cartItem.specialInstructions || "",
      })),
    };

    const merchantId = getMerchantIdOrThrow();

    const { data } = await apiCustomer.post(`/carts/by-merchant/${merchantId}/items`, cartData);
    return data;
  },

  async loadCart(reconstructionData?: {
    items: Item[];
    itemOffers: ItemOffer[];
    optionGroups: OptionGroup[];
    basicInfo: BasicInfo | null;
  }): Promise<Cart | null> {
    if (!cartSyncEnabled) return null;

    const merchantId = getMerchantIdOrThrow();

    try {
      const response = await apiCustomer.get(`/carts/by-merchant/${merchantId}`);

      const data = response.data;

      if (!data || !data.items) {
        return null;
      }

      if (
        reconstructionData &&
        CartReconstructionService.validateReconstructionData(
          reconstructionData.items,
          reconstructionData.itemOffers,
          reconstructionData.optionGroups
        )
      ) {
        const reconstructedCart = CartReconstructionService.reconstructCartFromBackend(
          data,
          reconstructionData.items,
          reconstructionData.itemOffers,
          reconstructionData.optionGroups,
          reconstructionData.basicInfo
        );

        return reconstructedCart;
      }

      return createEmptyCart();
    } catch (error) {
      return null;
    }
  },

  async clearCart(): Promise<void> {
    if (!cartSyncEnabled) return;

    const merchantId = getMerchantIdOrThrow();

    await apiCustomer.delete(`/carts/by-merchant/${merchantId}/items/clear`);
  },

  async updateCartItemQuantity(itemId: string, quantity: number): Promise<void> {
    if (!cartSyncEnabled) return;

    const merchantId = getMerchantIdOrThrow();

    const dataRequest = {
      id: itemId,
      quantity: quantity,
    };

    await apiCustomer.patch(`/carts/by-merchant/${merchantId}/items`, dataRequest);
  },

  async removeCartItem(itemId: string): Promise<void> {
    if (!cartSyncEnabled) return;

    const merchantId = getMerchantIdOrThrow();

    await apiCustomer.delete(`/carts/by-merchant/${merchantId}/items/${itemId}`);
  },

  async addCartItem(item: CartItemData): Promise<{ id: string }> {
    if (!cartSyncEnabled) return { id: "" };

    const merchantId = getMerchantIdOrThrow();

    const cartData = {
      items: [
        {
          id: item.item?.id || "",
          categoryId: item.categoryId,
          itemOfferId: item.itemOfferId,
          quantity: item.quantity,
          options: item.options.filter((opt) => opt.quantity > 0).map((opt) => ({
            ...opt,
            id: item.itemOptions?.[opt.optionId]?.id || "",
          })),
          specialInstructions: item.specialInstructions || "",
        },
      ],
    };

    const { data } = await apiCustomer.post(
      `/carts/by-merchant/${merchantId}/items`,
      cartData
    );

    return data;
  },

};
